// Supported default MIME types
export const MimeTypes = {
	image: 'image/png',
	audio: 'audio/mp3'
} as const

// Union type of supported default MIME values (e.g., 'image/png', 'audio/mp3')
export type MimeType = (typeof MimeTypes)[keyof typeof MimeTypes]

/**
 * Checks if a given string is a valid URL.
 * This uses the URL constructor, which supports all protocols (http, https, blob, etc.)
 * @param str - The string to validate as a URL
 * @returns boolean indicating whether the string is a valid URL
 */
export const isValidUrl = (str: string): boolean => {
	try {
		new URL(str)
		return true
	} catch {
		return false
	}
}

/**
 * Converts a base64 string to a valid data URI string, or passes through valid URLs or data URIs.
 * Supports images and audio. Defaults to image/png or audio/mp3 if type is unspecified.
 *
 * @param src - The source string, either a URL, data URI, or raw base64
 * @param type - The content type ('image' or 'audio'), defaults to 'image'
 * @param mimeTypeOverride - Optional MIME type override (e.g., 'image/jpeg', 'audio/ogg')
 * @returns a safe `src` string usable in <img>, <audio>, etc.
 */
export const getSafeSrc = (
	src?: string,
	type: 'image' | 'audio' = 'image',
	mimeTypeOverride?: string
): string | undefined => {
	if (!src) return undefined

	// Return as-is if already a full data URI or a valid URL
	if (src.startsWith('data:') || isValidUrl(src)) {
		return src
	}

	// Use provided MIME type override or fallback based on type
	const mimeType = mimeTypeOverride ?? MimeTypes[type] ?? 'application/octet-stream'
	return `data:${mimeType};base64,${src}`
}
